import { Component, Input, OnChanges, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from "@angular/core";
import { PicklistConfigService } from "config/components/picklists/picklist-config.service";
import { cloneDeep, first, isEqual } from "lodash";
import { ICharacteristicMetaDataCollection } from "rl-common/models/i-characteristic-meta-data-collection";
import { ICharacteristicValue } from "rl-common/models/i-characteristic-value";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharTypeId, SystemIndicators } from "rl-common/rl-common.consts";
import { AllocationType } from "rl-common/services/accounting-processes/models/accounting-process-allocation-type";
import { IAccountingOperationEvent } from "rl-common/services/accounting-processes/models/i-accounting-operation-event";
import { CompanyService } from "rl-common/services/company/company.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { AccountingProcesses } from "../accounting-processes.consts";
import { IAccountOrGroupId } from "../build-accounting-operation/models/i-account-or-group-id";

export interface ICreditDebitAccountOrGroup {
	creditName: string;
	creditId: IAccountOrGroupId;
	debitName: string;
	debitId: IAccountOrGroupId;
	allocationType: number;
	feeType?: number;
	feeTypeCharVal?: ICharacteristicValue;
}

@Component({
	selector: "rl-account-operation-event",
	templateUrl: "./account-operation-event.component.html",
	styleUrls: ["./account-operation-event.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: []
})
export class AccountOperationEventComponent implements OnChanges, OnDestroy, OnInit {
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _companyService = inject(CompanyService);
	private readonly _picklistConfigService = inject(PicklistConfigService);


	@Input()
	anchorTemplateId: number;

	@Input()
	event: IAccountingOperationEvent;

	@Input()
	hasCostAllocation: boolean;

	@Input()
	hasCostAllocationTemplate: boolean;

	@Input()
	operationType: number = null;

	anchorTemplate: ICharacteristicMetaDataCollection;

	get anchorTemplateLabel(): string {
		return this.anchorTemplate?.template?.templateName;
	}

	initialStatusLabel: string;
	wfActionLabel: string;
	resultingStatusLabel: string;

	revCreditDebitAccountOrGroups: ICreditDebitAccountOrGroup[];
	costCreditDebitAccountOrGroups: ICreditDebitAccountOrGroup[];
	displayFeeType: boolean = false;

	AllocationType = AllocationType;
	feeTypeItems: ICharacteristicValue[];
	readonly defaultFeeType = { charValueID: 0, charValueLabel: "None" } as ICharacteristicValue;

	private readonly _subs: Subscription[] = [];

	ngOnInit() {
		this.displayFeeType = !!this.operationType && this.operationType === AccountingProcesses.OperationType.CashRelevant;
	}

	async ngOnChanges(changes: ComponentChanges<this>): Promise<void> {
		if (changes.anchorTemplateId && changes.anchorTemplateId.currentValue && changes.anchorTemplateId.currentValue !== changes.anchorTemplateId.previousValue) {
			this.anchorTemplate = this._oneConfig.getTemplateMetaData(CharTypeId.Amount, this.anchorTemplateId);
			this.updateLabels();
		}

		if (changes.event && changes.event.currentValue && !isEqual(changes.event.currentValue, changes.event.previousValue)) {
			const accountOrGroups = this.event.accountOrGroups ?? [];

			this.revCreditDebitAccountOrGroups = accountOrGroups.filter(x => x.allocationType === AllocationType.Revenue).map(x => ({ creditName: x.creditName, creditId: x.creditId, debitName: x.debitName, debitId: x.debitId, allocationType: x.allocationType, feeType: x.feeType } as ICreditDebitAccountOrGroup));

			this.costCreditDebitAccountOrGroups = accountOrGroups.filter(x => x.allocationType === AllocationType.Cost).map(x => ({ creditName: x.creditName, creditId: x.creditId, debitName: x.debitName, debitId: x.debitId, allocationType: x.allocationType, feeType: x.feeType } as ICreditDebitAccountOrGroup));

			await this.setFeeTypeItems();
			if (this.feeTypeItems.length > 0) {
				this.setFeeTypeCharVals(this.revCreditDebitAccountOrGroups);
			}

			this.updateLabels();
		}
	}

	async setFeeTypeItems() {
		this.feeTypeItems = [];
		const feeType = this._oneConfig.getCharBySystemIndicator(CharTypeId.Amount, SystemIndicators.FeeType);
		if (feeType) {
			this.feeTypeItems = cloneDeep(await this._picklistConfigService.getPicklistSourceValues(feeType.charValueSetID).toPromise());
			this.feeTypeItems.unshift(this.defaultFeeType);
		}
	}

	setFeeTypeCharVals(groups: ICreditDebitAccountOrGroup[]) {
		if (this.feeTypeItems) {
			groups.forEach(g => g.feeTypeCharVal = this.feeTypeItems.find(f => f.charValueID === g.feeType));
		}
	}

	private updateLabels() {
		if (!this.anchorTemplate) {
			this.initialStatusLabel = `Unknown Status`;
			this.wfActionLabel = `Unknown Action`;
			this.resultingStatusLabel = `Unknown Status`;
			return;
		}
		const statuses = this._oneConfig.getTemplateStatuses(this.anchorTemplate.charTypeID, this.anchorTemplate.templateID);
		if (this.event.statusId == null) {
			this.initialStatusLabel = `None`;
		} else {
			const initialStatus = statuses.find(x => x.stepId === this.event.statusId);
			this.initialStatusLabel = initialStatus?.stepName ?? `Unknown Status`;
		}

		const wfProcessId = this._oneConfig.getTemplateProcess(this.anchorTemplate.charTypeID, this.anchorTemplate.templateID);
		if (this.event.workflowActionId == null) {
			this.wfActionLabel = `None`;
			this.resultingStatusLabel = `None`;
		} else {
			const wfAction = this._oneConfig.getWorkflowAction(this.anchorTemplate.charTypeID, wfProcessId, this.event.workflowActionId);
			this.wfActionLabel = wfAction?.actionName ?? `Unknown Action`;
			this.updateStatusLabel(wfProcessId, wfAction?.actionId);
		}
	}

	private updateStatusLabel(processId: number, actionId: number) {
		if (!actionId || !processId) {
			this.resultingStatusLabel = "";
		}
		const statuses = this._oneConfig.getTemplateStatuses(this.anchorTemplate.charTypeID, this.anchorTemplate.templateID);
		const sub = this._companyService.getWfActionStepAssociations(processId, actionId).pipe(
			map(associations => statuses.filter(step => associations.find(assoc => assoc.stepID === step.stepId)))
		).subscribe(steps => {
			this.resultingStatusLabel = first(steps)?.stepName ?? "No Status Change";
		});

		this._subs.push(sub);
	}

	ngOnDestroy() {
		this._subs.forEach(sub => sub.unsubscribe());
	}

}
